import React, { useEffect, useState } from "react";
import { Card, CardContent, CardTitle } from "../../misc/Card";
import { CampaignCardListingProps, CampaignCardProps } from "./types";
import CampaignStatusCard from "../../campaign/CampaignStatusCard";
import { Button } from "../../misc/Button";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import CustomToast from "../../misc/CustomToast";
import { useDispatch } from "react-redux";
import { setSelectedListingAddress } from "../../../store/calendarSlice";
import { OptionType } from "../../../types/option-type";
import { useIsMobile } from "../../../hooks/useIsMobile";
import SimpleTooltip from "../../misc/SimpleTooltip";
import { useAutopilotEnabled } from "../../../hooks/useAutopilotEnabled";
import { getApiErrorMessage } from "../../../utils/apiError";
import {
    isAutopilotManagedListing,
    isAutopilotManagedListingPending,
} from "../../../utils/autopilotManagedListing";
import {
    AutoVideoUnavailableReason,
    ListingType,
} from "../../../types/userListing";
import { AutopilotResponse } from "../../../types/autopilot";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../misc/Select";
import { Label } from "../../misc/Label";
import { getAuthHeader } from "../../../utils/authHeader";
import axios from "axios";
import { AlertTriangle } from "lucide-react";
import { useAddPostNavigation } from "../../add-post/useAddPostNavigation";

// Mirrors the API's MIN_CAMPAIGN_QUALIFIED_IMAGES — how many campaign-ready photos a
// listing needs before auto-video generation runs. Kept in step manually (no shared
// schema between api and ui).
const CAMPAIGN_QUALIFIED_IMAGES_REQUIRED = 12;

// MB-1314: the specific "not available" lead sentence for a NAMED ineligibility reason
// (property type named dynamically). Returned without the support-team line — the card
// appends that uniformly to every note. LISTING_TIER never reaches here; the card renders
// it as the generic fallback lead instead.
const autoVideoUnavailableMessage = (
    reason: AutoVideoUnavailableReason,
): string => {
    switch (reason.code) {
        case "PROPERTY_TYPE":
            return `Auto-generated slideshow video isn't available for this property type${
                reason.propertyType ? ` (${reason.propertyType})` : ""
            }.`;
        case "NEW_CONSTRUCTION":
            return "Auto-generated slideshow video isn't available for new construction listings.";
        case "MANUFACTURED_HOME":
        default:
            return "Auto-generated slideshow video isn't available for manufactured or mobile homes.";
    }
};

const CampaignCard: React.FC<CampaignCardProps> = ({
    allListing,
    mlsApproved,
    isUploading = false,
    setPropertyData,
    setOriginalPropertyData,
    setLoading,
    fetchListing,
    isSyncing,
    isVideo,
    campaignQualifiedImageCount,
    autoGenerateSlideshow = false,
    autoVideoUnavailableReason,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isMobile = useIsMobile(1200);
    // Add Post opens the new page on Listing Post while its switch is on; off, the old modal.
    const { enabled: addPostPageEnabled, openAddPost } = useAddPostNavigation();
    // MB-1436: Autopilot creates this listing's campaign itself - the manual
    // button is hidden (the api also 409s as the authority). Until the flag
    // resolves the action is withheld rather than offered and withdrawn.
    const { autopilotEnabled, loading: autopilotLoading } =
        useAutopilotEnabled();
    const autopilotManaged = isAutopilotManagedListing(
        autopilotEnabled,
        allListing ?? {},
    );
    const autopilotPending = isAutopilotManagedListingPending(
        autopilotLoading,
        allListing ?? {},
    );

    // MB-1472: per-listing pricing override, offered only for commercial
    // listings Autopilot itself manages - manual campaigns and non-commercial
    // listings never generate the pricing line this overrides.
    const showPricingOverride = Boolean(
        allListing?.managedByAutopilot && allListing?.marketingCategory,
    );
    // The account-wide default (Settings > Autopilot). Assumed on until the
    // fetch settles - the api defaults it true, so this avoids a flash of
    // "(Off)" for the common case while the request is in flight.
    const [commercialUsePrice, setCommercialUsePrice] = useState(true);
    const [savingPricingOverride, setSavingPricingOverride] = useState(false);

    useEffect(() => {
        if (!showPricingOverride) return;
        let cancelled = false;
        (async () => {
            try {
                const headers = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const response = await axios.get<AutopilotResponse>(
                    `${backendUrl}/settings/autopilot`,
                    { headers },
                );
                if (!cancelled) {
                    setCommercialUsePrice(
                        response.data?.settings?.commercialUsePrice !== false,
                    );
                }
            } catch (error) {
                // Purely a label for the "Account default" option - a failed
                // fetch just keeps the assumed-on default rather than
                // blocking the control or the rest of the card.
                console.error("Failed to load pricing default:", error);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [showPricingOverride]);

    // The displayed value comes from the listing prop, which only updates once
    // the PATCH and the refetch have both landed. Without an optimistic value
    // the dropdown snaps back to the old selection for the whole round trip.
    const [pendingPricingOverride, setPendingPricingOverride] = useState<
        "default" | "show" | "hide" | null
    >(null);

    const storedPricingOverrideValue: "default" | "show" | "hide" =
        allListing?.pricingOverride === "show"
            ? "show"
            : allListing?.pricingOverride === "hide"
              ? "hide"
              : "default";

    const pricingOverrideValue =
        pendingPricingOverride ?? storedPricingOverrideValue;

    const handlePricingOverrideChange = async (value: string) => {
        if (!allListing?.id) return;
        const mapped: "show" | "hide" | null =
            value === "show" ? "show" : value === "hide" ? "hide" : null;
        setPendingPricingOverride(value as "default" | "show" | "hide");
        setSavingPricingOverride(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            await axios.patch(
                `${backendUrl}/listing/${allListing.id}/pricing-override`,
                { pricingOverride: mapped },
                { headers },
            );
            toast.success(
                <CustomToast
                    message="Saved - applies at the next weekly renewal"
                    type="success"
                />,
                { autoClose: 3000 },
            );
            await fetchListing();
        } catch (error: unknown) {
            console.error(error);
            toast.error(
                <CustomToast
                    message={getApiErrorMessage(
                        error,
                        "Failed to update pricing.",
                    )}
                    type="error"
                />,
                { autoClose: 3000 },
            );
        } finally {
            // Cleared last: on success the refetched listing already carries the
            // new value, and on failure this reverts the dropdown to the truth.
            setPendingPricingOverride(null);
            setSavingPricingOverride(false);
        }
    };

    // Only surface when it's actionable: auto-video is on but the listing is still
    // short on campaign-ready photos. Hides itself once enough good photos are
    // uploaded and tagged.
    const showQualifiedNote =
        autoGenerateSlideshow &&
        typeof campaignQualifiedImageCount === "number" &&
        campaignQualifiedImageCount < CAMPAIGN_QUALIFIED_IMAGES_REQUIRED;

    const supportLink = (
        <button
            type="button"
            onClick={() => navigate("/help", { state: { tab: "contact" } })}
            className="font-medium underline"
        >
            contact our support team
        </button>
    );

    // MB-1314: one auto-video note, chosen by priority so the reasons never fight for the
    // same slot. Named reason (property type / new construction / manufactured) wins — it's
    // permanent, so adding photos wouldn't help. Then the actionable not-enough-photos note.
    // Then the generic fallback for anything else that stops generation (e.g. a tier block).
    // Every note ends with the same support-team line (appended below), for consistency.
    let noteLead: string | null = null;
    if (
        autoVideoUnavailableReason &&
        autoVideoUnavailableReason.code !== "LISTING_TIER"
    ) {
        noteLead = autoVideoUnavailableMessage(autoVideoUnavailableReason);
    } else if (showQualifiedNote) {
        noteLead =
            "Auto-generated slideshow video can't be created because there aren't enough campaign-ready images.";
    } else if (autoVideoUnavailableReason) {
        noteLead =
            "Auto-generated slideshow video can't be created for this listing.";
    }

    const campaignNote: React.ReactNode = noteLead ? (
        <>
            {noteLead}
            {" Please "}
            {supportLink}
            {" for help."}
        </>
    ) : null;

    const handleViewCampaign = () => {
        if (allListing?.formattedAddress) {
            const address = allListing.formattedAddress.split(",")[0].trim();
            const option: OptionType[] = [
                {
                    label: address.charAt(0).toUpperCase() + address.slice(1),
                    value: address,
                },
            ];
            dispatch(setSelectedListingAddress(option));
            navigate("/");
        }
    };

    const handleAddPost = () => {
        // The card picks the listing from ?listing=, so the calendar filter below is not needed.
        if (addPostPageEnabled) {
            openAddPost("listing", allListing?.campaignId);
            return;
        }
        if (allListing?.formattedAddress) {
            const address = allListing.formattedAddress.split(",")[0].trim();
            const option: OptionType[] = [
                {
                    label: address.charAt(0).toUpperCase() + address.slice(1),
                    value: address,
                },
            ];
            dispatch(setSelectedListingAddress(option));
            navigate("/", { state: { addPost: true } });
        }
    };

    const CampaignCardListing: CampaignCardListingProps = {
        campaignProgress: allListing?.campaignProgress,
        campaignStatus: allListing?.campaignStatus,
        campaignType: allListing?.campaignType,
        generatedPosts: allListing?.generatedPosts,
        publishedPosts: allListing?.publishedPosts,
        scheduledPosts: allListing?.scheduledPosts,
        unapprovedPosts: allListing?.unapprovedPosts,
        contentRefresh: allListing?.contentRefresh,
        generatingVideoCount: allListing?.generatingVideoCount,
        autoVideoPostsTotal: allListing?.autoVideoPostsTotal,
        autoVideoPostsDone: allListing?.autoVideoPostsDone,
    };

    const handleCampaignRetry = async (campaignId?: string) => {
        setLoading(true);
        if (!campaignId) return;
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const response = await axios.get(
                `${backendUrl}/listing/restart-campaign/${campaignId}`,
                { headers }
            );
            if (response.status === 200) {
                toast.success(
                    <CustomToast
                        message="Campaign generation process restarted."
                        type="success"
                    />,
                    { autoClose: 3000 }
                );
                setPropertyData(response.data);
                setOriginalPropertyData(response.data);
                fetchListing();
            }
        } catch (error: unknown) {
            console.error(error);
            toast.error(
                <CustomToast
                    message={getApiErrorMessage(
                        error,
                        "Failed to retry campaign."
                    )}
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setLoading(false);
        }
    };

    return (
      <div className={`${isMobile ? "mt-2" : "mt-0"} relative`}>
        {" "}
        {/* make wrapper relative */}
        <Card>
          <CardContent className="pt-6">
            <CardTitle className="mb-4">Campaign</CardTitle>
            {campaignNote && (
                <div className="mb-4 flex items-start gap-2 rounded-md border border-secondary bg-secondary/10 p-3 text-left text-xs text-primary">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{campaignNote}</span>
                </div>
            )}
            <CampaignStatusCard listing={CampaignCardListing} />

            {/* MB-1436/MB-1441: no manual campaigns under Autopilot - explain
                instead of offering a create the api would refuse. */}
            {!allListing?.campaignStatus && autopilotManaged && (
                <div className="mt-4 flex items-start gap-2 rounded-md border border-secondary bg-secondary/10 p-3 text-left text-xs text-primary">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>
                        Auto Pilot is on - listing campaigns are created and
                        scheduled automatically, so manual campaign creation
                        is unavailable.
                    </span>
                </div>
            )}

            {/* MB-1472: commercial listings under Autopilot only - lets the
                owner override the account's pricing default for this one
                listing, independent of campaign status. */}
            {showPricingOverride && (
                <div className="mt-4">
                    <Label
                        htmlFor="pricing-override-select"
                        className="text-primary"
                    >
                        Pricing in posts
                    </Label>
                    <Select
                        value={pricingOverrideValue}
                        onValueChange={handlePricingOverrideChange}
                        disabled={savingPricingOverride || isSyncing}
                    >
                        <SelectTrigger
                            id="pricing-override-select"
                            className="mt-1"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="default">
                                {`Account default (${
                                    commercialUsePrice ? "On" : "Off"
                                })`}
                            </SelectItem>
                            <SelectItem value="show">Always show</SelectItem>
                            <SelectItem value="hide">Never show</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            )}

            {/* Create Campaign Button */}
            {!allListing?.campaignStatus &&
              !autopilotManaged &&
              !autopilotPending && (
              <SimpleTooltip
              className="w-full"
                message="Please wait for images and/or videos to be done uploaded and processed before creating a campaign."
                visible={isUploading}
              >
                <Button
                  className="w-full mt-4"
                  onClick={() => {
                    if (allListing.imageCount <= 0) {
                      toast.error(
                        <CustomToast
                          message="Please upload images before creating a campaign."
                          type="error"
                        />,
                        { autoClose: 3000 },
                      );
                    } else {
                      navigate("/create-campaign", {
                        state: {
                          listingId: allListing.id,
                          listingType:
                            mlsApproved &&
                            allListing.listingType === ListingType.MLSListing
                              ? "MLSListing"
                              : "Listing",
                          from: location.pathname,
                          parsedFooter: allListing.parsedFooter,
                          parsedGoogleFooter: allListing.parsedGoogleFooter,
                          isVideo: isVideo,
                          usableImageCount: allListing.usableImageCount,
                        },
                      });
                    }
                  }}
                  disabled={isUploading || isSyncing}
                >
                  Create Campaign
                </Button>
              </SimpleTooltip>
            )}

            {/* View & Add Post Buttons for Active Campaign */}
            {(allListing?.campaignStatus === "ACTIVE" ||
              allListing?.campaignStatus === "PAUSED") && (
              <>
                <Button
                  id="view-campaign-btn"
                  className="mt-4 w-full"
                  variant="secondary"
                  onClick={handleViewCampaign}
                >
                  View Campaign Schedule
                </Button>

                <Button
                  id="add-post-btn"
                  className="mt-4 w-full"
                  onClick={handleAddPost}
                  disabled={isSyncing}
                >
                  Add Post
                </Button>

                {/* MB-1371: re-enter the wizard in edit mode. Changes are
                    staged and apply at the next campaign renewal, so the
                    button is ACTIVE-only — a paused campaign never reaches a
                    renewal, and the edit would sit there unapplied. Also
                    hidden for Autopilot-managed campaigns — the API refuses
                    to edit them (their cadence is owned by Autopilot
                    settings). */}
                {allListing?.campaignStatus === "ACTIVE" &&
                  !allListing?.managedByAutopilot && (
                  <Button
                    id="edit-campaign-settings-btn"
                    className="mt-4 w-full"
                    variant="secondary"
                    onClick={() =>
                      navigate("/edit-campaign", {
                        state: {
                          editCampaignId: allListing.campaignId,
                          listingId: allListing.id,
                          listingType:
                            mlsApproved &&
                            allListing.listingType === ListingType.MLSListing
                              ? "MLSListing"
                              : "Listing",
                          from: location.pathname,
                          parsedFooter: allListing.parsedFooter,
                          parsedGoogleFooter: allListing.parsedGoogleFooter,
                          usableImageCount: allListing.usableImageCount,
                        },
                      })
                    }
                    disabled={isSyncing}
                  >
                    Edit Campaign Settings
                  </Button>
                )}
              </>
            )}

            {/* Retry on Error */}
            {allListing?.campaignStatus === "ERROR" && (
              <Button
                id={`retry-campaign-${allListing.campaignId}`}
                className="mt-4 w-full"
                onClick={() => handleCampaignRetry(allListing.campaignId)}
                disabled={isSyncing}
              >
                Retry Campaign
              </Button>
            )}

            {/* Pause Message */}
            {allListing?.campaignStatus === "SOCIAL_PAUSE" && (
              <div className="mt-2 flex justify-center">
                <span className="text-center text-red-500">
                  Connect Social Media platform(s) from Settings to resume
                  campaign
                </span>
              </div>
            )}

            {/* MB-1520: the system paused this campaign (D2) - say why, so
                the agent knows it was not their own pause. */}
            {allListing?.campaignStatus === "PAUSED" &&
                allListing?.campaignPauseReason && (
                    <div className="mt-4 flex items-start gap-2 rounded-md border border-secondary bg-secondary/10 p-3 text-left text-xs text-primary">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span>{allListing.campaignPauseReason}</span>
                    </div>
                )}
          </CardContent>
        </Card>
      </div>
    );
};

export default CampaignCard;
